import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import { AnalysisResult } from '@paragon/contract-library';

type AnalysisResultLoadingStatus =
  { status: 'loading' } | { status: 'loaded'; item: AnalysisResult } | { status: 'error' };

type AnalysisResultRequests = { [guid: string]: AnalysisResultLoadingStatus | undefined };

const initialState: AnalysisResultRequests = {};

const analysisResultRequestsSlice = createSlice({
  name: 'analysisResultRequests',
  initialState,
  reducers: {
    fetchAnalysisResultRequested: (state, action: PayloadAction<{ analysisResultGuids: string[] }>) => {
      for (const guid of action.payload.analysisResultGuids) {
        state[guid] = { status: 'loading' };
      }
    },
    fetchAnalysisResultSucceeded: (state, action: PayloadAction<AnalysisResult>) => {
      const analysisResult = action.payload;

      if (state[analysisResult.guid]?.status !== 'loaded') {
        state[analysisResult.guid] = { status: 'loaded', item: castDraft(analysisResult) };
      }
    },
    fetchAnalysisResultCanceled: (state, action: PayloadAction<{ analysisResultGuids: string[] }>) => {
      for (const guid of action.payload.analysisResultGuids) {
        if (state[guid]?.status !== 'loaded') {
          delete state[guid];
        }
      }
    },
    fetchAnalysisResultFailed: (state, action: PayloadAction<{ analysisResultGuid: string }>) => {
      const { analysisResultGuid } = action.payload;

      if (state[analysisResultGuid]?.status === 'loading') {
        state[analysisResultGuid] = { status: 'error' };
      }
    },
    clearAnalysisResults: (state, action: PayloadAction<{ guidsOfAnalysisResultsToKeep: readonly string[] }>) => {
      for (const guid of Object.keys(state)) {
        if (!action.payload.guidsOfAnalysisResultsToKeep.includes(guid)) {
          delete state[guid];
        }
      }
    },
  },
});

export const {
  fetchAnalysisResultRequested,
  fetchAnalysisResultSucceeded,
  fetchAnalysisResultCanceled,
  fetchAnalysisResultFailed,
  clearAnalysisResults,
} = analysisResultRequestsSlice.actions;
export const analysisResultRequests = analysisResultRequestsSlice.reducer;
